import { Select, Input } from 'antd';
import classNames from 'classnames';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import BaseLoader from '../base-loader';
import InputSearchIcon from '../icons/inputSearchIcon';
import s from './styles.module.scss';

type Opt = {
  label: string;
  value: string;
  otherLabel?: string;
  isOther?: boolean;
};

const OTHER_VALUE = '__OTHER__';

export type BaseSearchSelectWithOtherProps = {
  options: Opt[];
  value?: string;
  suffixIcon?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  onSelectValue: (value?: string) => void;
  onOtherChange?: (other?: string) => void;
  otherValue?: string;
  className?: string;
};

const BaseSearchSelectWithOther = ({
  options,
  value,
  suffixIcon,
  placeholder,
  disabled,
  isLoading,
  onSelectValue,
  onOtherChange,
  otherValue,
  className,
}: BaseSearchSelectWithOtherProps) => {
  const { t } = useTranslation();

  const [showOtherInput, setShowOtherInput] = useState(false);
  const [otherInputValue, setOtherInputValue] = useState('');

  const otherContainerRef = useRef<HTMLDivElement | null>(null);
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);

  const mergedOptions = useMemo<Opt[]>(() => {
    const cleaned = (options ?? []).filter((o) => !o.isOther);

    if (otherValue?.trim()) {
      const v = otherValue.trim();
      return [
        ...cleaned,
        {
          label: v,
          value: v,
          isOther: true,
          otherLabel: `Other (${v})`,
        },
      ];
    }

    return [...cleaned, { label: 'Other', value: OTHER_VALUE, isOther: true }];
  }, [options, otherValue]);

  const handleSelect = (val: string) => {
    if (val === OTHER_VALUE) {
      setOtherInputValue(otherValue ?? '');
      setShowOtherInput(true);
      onSelectValue(undefined);
      return;
    }

    if (otherValue && val === otherValue.trim()) {
      setOtherInputValue(otherValue.trim());
      setShowOtherInput(true);
      onSelectValue(val);
      return;
    }

    setShowOtherInput(false);
    onSelectValue(val);
  };

  const handleSaveOther = () => {
    const v = otherInputValue.trim();

    if (!v) {
      setShowOtherInput(false);
      setOtherInputValue('');
      onOtherChange?.(undefined);
      onSelectValue(undefined);
      return;
    }

    onOtherChange?.(v);
    onSelectValue(v);
    setShowOtherInput(false);
    setOtherInputValue('');
  };

  const handleCancelOther = () => {
    setShowOtherInput(false);
    setOtherInputValue('');
    if (otherValue) onSelectValue(otherValue);
  };

  const handleClear = () => {
    onSelectValue(undefined);
    onOtherChange?.(undefined);
  };

  const filterSelectOption = useCallback((input: string, option: any) => {
    const opt = option as { label?: string; value?: string };
    const text = (opt.label ?? opt.value ?? '').toString().toLowerCase();
    return text.includes(input.toLowerCase());
  }, []);

  useEffect(() => {
    if (!showOtherInput) return;

    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;

      if (cancelBtnRef.current?.contains(target)) return;

      if (
        otherContainerRef.current &&
        !otherContainerRef.current.contains(target)
      ) {
        handleSaveOther();
      }
    };

    document.addEventListener('mousedown', onMouseDown, true);
    return () => document.removeEventListener('mousedown', onMouseDown, true);
  }, [showOtherInput, otherInputValue]);

  const renderSuffixIcon = () => {
    if (value) return null;

    if (suffixIcon) return suffixIcon;

    return <InputSearchIcon />;
  };

  return (
    <div className={s.wrapper}>
      {!showOtherInput ? (
        <Select
          style={{ width: '100%' }}
          popupClassName="certain-category-search-dropdown"
          size="middle"
          options={mergedOptions}
          placeholder={placeholder}
          disabled={disabled}
          showSearch
          allowClear
          filterOption={filterSelectOption}
          suffixIcon={renderSuffixIcon()}
          notFoundContent={
            isLoading ? (
              <BaseLoader loading={Boolean(isLoading)} height={20} width={20} />
            ) : (
              t('labels.bannerNoData')
            )
          }
          onSelect={handleSelect}
          onClear={handleClear}
          value={value}
          optionRender={(option) => {
            const opt = option.data as Opt;
            return (
              <div data-cy="autocomplete-option">
                {opt.otherLabel ?? opt.label}
              </div>
            );
          }}
          className={classNames(s['search-input'], className)}
        />
      ) : (
        <div ref={otherContainerRef} className={s['other-input-container']}>
          <Input
            value={otherInputValue}
            onChange={(e) => setOtherInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSaveOther();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                handleCancelOther();
              }
            }}
            placeholder="Enter custom value"
            autoFocus
            className={s['other-input']}
          />
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={handleCancelOther}
            className={s['close-button']}
            aria-label="Cancel"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default BaseSearchSelectWithOther;
